import React from 'react';
import { useSelector } from 'react-redux';
import { Modal } from '../CommonComponents/OPL/Modal/Modal';
import { ModalHeader } from '../CommonComponents/OPL/Modal/ModalHeader';
import { ModalCloseButton } from '../CommonComponents/OPL/Modal/ModalCloseButton';
import { Box } from '../CommonComponents/OPL/Box/Box';
import { ErrorState } from '../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { currentClientSelector } from '../AdviserArea/AdviserAreaSelector';
import { SaveParams } from './ClientAccess.types';
import { useSaveClientAccessMutation } from './api/useSaveClientAccessMutation';
import { toast } from '../CommonComponents/OPL/Toast/Toast';
import { MESSAGES } from '../CommonConstants';
import { ClientAccessLoading } from './ClientAccess.loading';
import { ConfigureAccess } from './components/ConfigureAccess/ConfigureAccess';
import { InviteClient } from './components/InviteClient/InviteClient';
import { useClientAccess } from './useClientAccess';
import { OnboardingWorkflow } from '../CommonSelectors/CommonArea.types';

interface Props {
  clientId: string;
  onClose(): void;
  defaultOnboardingWorkflow?: OnboardingWorkflow;
}

export const ClientAccess = (props: Props) => {
  const currentClient = useSelector(currentClientSelector);

  if (!currentClient) {
    return null;
  }

  const { onClose } = props;

  return (
    <Modal
      isOpen
      onRequestClose={onClose}
      shouldCloseOnOverlayClick={false}
      aria={{ labelledby: 'client-access-modal--header' }}
    >
      <ModalHeader id="client-access-modal--header" as="h2" hasBorder={currentClient.IsRegistered}>
        {currentClient.IsRegistered ? 'Client Access' : 'Invite to Orion Planning'}
      </ModalHeader>
      <ModalCloseButton onClick={onClose} />

      <ClientAccessContent {...props} isClientRegistered={currentClient.IsRegistered} />
    </Modal>
  );
};

interface ContentProps extends Props {
  isClientRegistered: boolean;
}

const ClientAccessContent = ({
  clientId,
  isClientRegistered,
  onClose,
  defaultOnboardingWorkflow,
}: ContentProps) => {
  const { isLoading, isError, refetch, data } = useClientAccess({
    clientId,
    defaultOnboardingWorkflow,
  });

  const {
    mutateAsync: saveClientAccess,
    isLoading: isSavingClientAccess,
  } = useSaveClientAccessMutation();

  if (isLoading) {
    return <ClientAccessLoading />;
  }

  if (isError) {
    return (
      <Box display="flex" justifyContent="center" my="56">
        <ErrorState onAction={refetch} />
      </Box>
    );
  }

  if (!data) {
    return null;
  }

  const handleSave = async ({ onSuccess, isSaveAndPreview }: SaveParams = {}) => {
    try {
      await saveClientAccess({ clientId, ...data });

      if (onSuccess) {
        onSuccess();
      }

      if (!isSaveAndPreview) {
        toast.success('Successfully Saved');
      }
    } catch (err) {
      console.error(err);
      toast.error(MESSAGES.GENERIC_ERROR);
    }
  };

  const Component = isClientRegistered ? ConfigureAccess : InviteClient;

  return (
    <Component
      clientId={clientId}
      data={data}
      onSave={handleSave}
      onClose={onClose}
      isClientRegistered={isClientRegistered}
      isSavingClientAccess={isSavingClientAccess}
      defaultOnboardingWorkflow={defaultOnboardingWorkflow}
    />
  );
};
